import type { FC } from 'react';
import { memo, useMemo, useState } from 'react';
import { Box, Button, Card, Collapse, Divider, IconButton, Typography } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LayersIcon from '@mui/icons-material/Layers';
import { DataGrid } from '@mui/x-data-grid';
import { endOfWeek, format, getISOWeek, startOfWeek, } from 'date-fns';
import type { AdsHistoryGroupedEntry } from './AdsHistoryGroupedView.mockData';
import { useAdsHistoryGroupedColumns } from '../hooks/useAdsHistoryGroupedColumns';
import AdsHistoryPackInfo from './AdsHistoryPackInfo';

interface PackGroup {
  packName: string;
  /** Representative entry for pack-level info (account, adset, email, date). */
  representative: AdsHistoryGroupedEntry;
  entries: AdsHistoryGroupedEntry[];
}

interface HypothesisGroup {
  hypothesis: string;
  packs: PackGroup[];
  totalAds: number;
}

interface WeekSection {
  /** ISO week key used as React key, e.g. "2026-W21". */
  weekKey: string;
  /** Displayed header, e.g. "2026-05-18 – 2026-05-24 – Week 21". */
  label: string;
  groups: HypothesisGroup[];
}

export type AdsHistoryGroupMode = 'packs' | 'hypothesis';

interface AdsHistoryGroupedViewProps {
  entries: AdsHistoryGroupedEntry[];
  mode: AdsHistoryGroupMode;
  emptyPlaceholder?: string;
  /** Show only this many groups per week initially; the rest are revealed via a "Show more packs" button. */
  initialVisibleGroups?: number;
}

function getWeekKey(date: Date): string {
  const week = getISOWeek(date);
  const year = startOfWeek(date, { weekStartsOn: 1 }).getFullYear();
  return `${year}-W${String(week).padStart(2, '0')}`;
}

function getWeekLabel(date: Date): string {
  const weekStart = startOfWeek(date, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(date, { weekStartsOn: 1 });
  const week = getISOWeek(date);
  return `${format(weekStart, 'yyyy-MM-dd')} – ${format(weekEnd, 'yyyy-MM-dd')} – Week ${week}`;
}

function groupEntriesByWeek(
  entries: AdsHistoryGroupedEntry[],
  mode: AdsHistoryGroupMode,
): WeekSection[] {
  // week → hypothesis → pack → entries
  const weekMap = new Map<string, { label: string; hypothesisMap: Map<string, Map<string, AdsHistoryGroupedEntry[]>> }>();

  for (const entry of entries) {
    const date = new Date(entry.created_at);
    const weekKey = getWeekKey(date);
    const weekLabel = getWeekLabel(date);

    if (!weekMap.has(weekKey)) {
      weekMap.set(weekKey, { label: weekLabel, hypothesisMap: new Map() });
    }
    const { hypothesisMap } = weekMap.get(weekKey)!;

    const hKey = mode === 'hypothesis' ? entry.hypothesis : entry.pack_name;
    if (!hypothesisMap.has(hKey)) hypothesisMap.set(hKey, new Map());
    const packMap = hypothesisMap.get(hKey)!;
    if (!packMap.has(entry.pack_name)) packMap.set(entry.pack_name, []);
    packMap.get(entry.pack_name)!.push(entry);
  }

  const sortedWeeks = Array.from(weekMap.entries()).sort(([a], [b]) =>
    b.localeCompare(a),
  );

  return sortedWeeks.map(([weekKey, { label, hypothesisMap }]) => {
    const groups: HypothesisGroup[] = Array.from(hypothesisMap.entries()).map(
      ([hypothesis, packMap]) => {
        const packs: PackGroup[] = Array.from(packMap.entries()).map(
          ([packName, packEntries]) => ({
            packName,
            representative: packEntries[0],
            entries: packEntries,
          }),
        );
        return { hypothesis, packs, totalAds: packs.reduce((s, p) => s + p.entries.length, 0) };
      },
    );
    return { weekKey, label, groups };
  });
}

const WeekSeparator: FC<{ label: string }> = ({ label }) => (
  <Box sx={{ mb: 2, mt: 1 }}>
    <Typography
      variant="subtitle2"
      fontWeight={600}
      sx={(t) => ({ color: t.palette.text.primary, mb: 0.5 })}
    >
      {label}
    </Typography>
    <Divider />
  </Box>
);

const dataGridSx = {
  border: 'none',
  '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
  '& .MuiDataGrid-columnHeaders': { borderTop: 1, borderColor: 'divider', bgcolor: 'transparent' },
  '& .MuiDataGrid-columnHeaderTitle': { fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' },
} as const;

const PackSection: FC<{ pack: PackGroup; isFirst: boolean }> = memo(({ pack, isFirst }) => {
  const columns = useAdsHistoryGroupedColumns();

  return (
    <Box sx={{ borderTop: isFirst ? 0 : 1, borderColor: 'divider' }}>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <LayersIcon fontSize="small" sx={{ color: 'text.primary' }} />
        <Typography variant="body2" fontWeight={500}>
          Pack: {pack.packName} ({pack.entries.length})
        </Typography>
      </Box>

      <DataGrid
        rows={pack.entries}
        columns={columns}
        getRowId={(row: AdsHistoryGroupedEntry) => row.id}
        rowHeight={60}
        hideFooter
        hideFooterPagination
        disableColumnMenu
        checkboxSelection={false}
        disableRowSelectionOnClick
        sx={{
          ...dataGridSx,
          '& .MuiDataGrid-columnHeaders': {
            ...dataGridSx['& .MuiDataGrid-columnHeaders'],
            borderTop: 0,
          },
        }}
      />

      <AdsHistoryPackInfo entry={pack.representative} />
    </Box>
  );
});

PackSection.displayName = 'PackSection';

const HypothesisCard: FC<{ group: HypothesisGroup; showHeader: boolean }> = memo(
  ({ group, showHeader }) => {
    const [expanded, setExpanded] = useState(true);

    return (
      <Card
        variant="outlined"
        sx={{ mb: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, boxShadow: 'none' }}
      >
        {showHeader && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: 1.5, pr: 2, py: 1 }}>
            <IconButton
              size="small"
              color="primary"
              onClick={() => { setExpanded((p) => !p); }}
            >
              {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              sx={{ cursor: 'pointer', color: 'primary.main' }}
              onClick={() => { setExpanded((p) => !p); }}
            >
              {group.hypothesis}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
              (Ads: {group.totalAds})
            </Typography>
          </Box>
        )}

        <Collapse in={expanded} unmountOnExit>
          {group.packs.map((pack, idx) => (
            <PackSection key={pack.packName} pack={pack} isFirst={idx === 0} />
          ))}
        </Collapse>
      </Card>
    );
  },
);

HypothesisCard.displayName = 'HypothesisCard';

const ShowMoreButton: FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
    <Button variant="outlined" onClick={onClick}>
      {label}
    </Button>
  </Box>
);

const WeekBlock: FC<{
  week: WeekSection;
  showHeader: boolean;
  initialVisibleGroups?: number;
}> = ({ week, showHeader, initialVisibleGroups }) => {
  const [showAll, setShowAll] = useState(false);
  const limit = showAll ? undefined : initialVisibleGroups;
  const groups = limit === undefined ? week.groups : week.groups.slice(0, limit);
  const hiddenCount = week.groups.length - groups.length;

  return (
    <Box>
      <WeekSeparator label={week.label} />
      {groups.map((group) => (
        <HypothesisCard key={group.hypothesis} group={group} showHeader={showHeader} />
      ))}
      {hiddenCount > 0 && (
        <ShowMoreButton
          label={`Show more packs +${hiddenCount}`}
          onClick={() => { setShowAll(true); }}
        />
      )}
    </Box>
  );
};

const AdsHistoryGroupedView: FC<AdsHistoryGroupedViewProps> = ({
  entries,
  mode,
  emptyPlaceholder,
  initialVisibleGroups,
}) => {
  const weekSections = useMemo(
    () => groupEntriesByWeek(entries, mode),
    [entries, mode],
  );

  if (weekSections.length === 0) {
    return (
      <Typography color="text.secondary" sx={(t) => ({ fontSize: t.typography.h1, color: t.palette.grey.A200, mb: 4 })}>
        {emptyPlaceholder ?? 'No ads history logs found.'}
      </Typography>
    );
  }

  return (
    <Box>
      {weekSections.map((week) => (
        <WeekBlock
          key={week.weekKey}
          week={week}
          showHeader={mode === 'hypothesis'}
          initialVisibleGroups={initialVisibleGroups}
        />
      ))}
    </Box>
  );
};

export default AdsHistoryGroupedView;
